import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { MaterialCharacteristics } from "@jasper/client-models";
import type { MaterialAndShadeCatalog } from "@jasper/use-cases";
import type { DescribableProduct } from "./describable.types";
import { useProductTranslations } from "@jasper/translations";
import { getOrderDetails, getOrderTitle, getProductsCountLabel } from "./order";
import {
  describeProduct,
  getMaterialLabel,
  getProductTypeLabel,
} from "./product";

/**
 * The product description helpers, in the user's language.
 *
 * Their labels come from the product-workflows dictionary: the hook binds them to its `t`,
 * so that pages describe products without handling translations themselves.
 */
export const useProductDescription = () => {
  const { t } = useTranslation("product-workflows");
  const { t: translateProduct } = useProductTranslations();

  return useMemo(
    () => ({
      describeProduct: (
        product: DescribableProduct,
        materialAndShadeCatalog: MaterialAndShadeCatalog
      ) =>
        describeProduct(product, materialAndShadeCatalog, t, translateProduct),
      getMaterialLabel: (
        materialCharacteristics: MaterialCharacteristics,
        materialAndShadeCatalog: MaterialAndShadeCatalog
      ) =>
        getMaterialLabel(materialCharacteristics, materialAndShadeCatalog, t),
      getProductTypeLabel: (product: DescribableProduct) =>
        getProductTypeLabel(product, translateProduct),
      getProductsCountLabel: (count: number) => getProductsCountLabel(count, t),
      getOrderTitle: (products: ReadonlyArray<DescribableProduct>) =>
        getOrderTitle(products, t, translateProduct),
      getOrderDetails: (
        products: ReadonlyArray<DescribableProduct>,
        materialAndShadeCatalog: MaterialAndShadeCatalog
      ) =>
        getOrderDetails(products, materialAndShadeCatalog, t, translateProduct),
    }),
    [t, translateProduct]
  );
};
